import { createContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import moment from 'moment'
import { isNumber } from 'lodash'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

import { selectUser } from '../reducers/UserReducer'
import { getAllEventCodes } from '../utils/api/eventCode'
import { getAllLanguages } from '../utils/api/language'
import { getAllSpecialities } from '../utils/api/speciality'
import { getProfileImage } from '../utils/api/users'
import { HTTP_NOT_FOUND } from '../utils/constants/http'
import { onError, resolvePromises } from '../utils/apiHelper'
import { getConstants } from '../utils/api/global'
import { CARPOOL_OPTIN_TEXTS, CARPOOL_TYPE, DATA_SHARING_TEXTS, DATA_SHARING_TYPE, UNSUPPORTED_TYPE } from '../utils/entities/optin'
import { getUserOptins, getUserUnreadMessages } from '../utils/api/user'
import { arrayToObject, getEnvironment, recursiveIncludes } from '../utils'
import {
  MACCS_CONTEXT,
  NURSE_ENVIRONMENT,
  NURSES_CONTEXT,
  InstitutionRoles,
  SchoolRoles,
  OtherRoles,
  Contexts,
  InstitutionOptions,
  LinkRequestStates
} from '../utils/constants'
import { getAllAreas } from '../utils/api/area'
import useLocalStorageOld from '../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS from '../utils/localStorage'
import { Routes } from '../routes'
import { Environments } from '../config/environments'
import { reduceBySection, sortBySectionAndYear } from '../utils/entities/sectionToYear'
import { getAllLinkRequests } from '../utils/api/linkRequest'
import { sortByAbbreviation } from '../utils/entities/schoolSection'
import { isAdminHospitalRO, isAdminReseau, isClinicalSecretary, isEncoder, isHospitalAdmin, isInstitutionGroupAdmin, isInternshipMasterMaccs, isLocalSecretary, isPresiMaster } from '../utils/roles'
import { sortByName } from '../utils/entities/institution'
import { selectTranslate } from '../reducers/LocaleReducer/LocalReducer'
import { getAllWorkingRegimes } from '../utils/api/workingRegime'
import { sortBySpecialityAndYear } from '../utils/entities/specialityYear'

const ENVIRONMENT_KEY = 'global_provider.environment'

const INVALID_STATUS = 0
const VALID_STATUS = 1

export const GlobalContext = createContext()

const GlobalProvider = ({ children }) => {
  const history = useHistory()

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [currentDate, setCurrentDate] = useState(moment().startOf('day'))
  const [countries, setCountries] = useState([])
  const [environment, setEnvironment] = useLocalStorageOld(ENVIRONMENT_KEY, NURSE_ENVIRONMENT)
  const [eventCodes, setEventCodes] = useState({ data: [], status: VALID_STATUS })
  const [institutions, setInstitutions] = useState([])
  const [isDraft, setIsDraft] = useState(false)
  const [languages, setLanguages] = useState([])
  const [optins, setOptins] = useState([])
  const [pauses, setPauses] = useState([])
  const [specialities, setSpecialities] = useState([])
  const [schoolYears, setSchoolYears] = useState([])
  const [sections, setSections] = useState([])
  const [sectionToYears, setSectionToYears] = useState([])
  const [studentMetadata, setStudentMetadata] = useState([])
  const [tutorials, setTutorials] = useState([])
  const [unSeenChangelogs, setUnseenChangelogs] = useState([])
  const [unreadMessages, setUnreadMessages] = useState(null)
  const [areas, setAreas] = useState([])
  const [loading, setLoading] = useState(true)
  const [profileImage, setProfileImage] = useState(null)
  const [modules, setModules] = useState({ [InstitutionOptions.RECRUITMENT_MODULE]: false, [InstitutionOptions.MODULE_SETTINGS]: false })
  const [pendingLinkRequests, setPendingLinkRequests] = useState([])
  const [workingRegimes, setWorkingRegimes] = useState([])

  const [betaMode, setBetaMode] = useLocalStorageOld(LOCAL_STORAGE_KEYS.MISC.BETA_MODE, false)

  const nursesMode = useMemo(() => NURSES_CONTEXT.includes(user.context), [user.context])
  const WIPZone = useMemo(() => getEnvironment() !== Environments.PROD, [])
  const context = useMemo(() => user?.context ?? Contexts.NONE, [user?.context])
  const currentRole = useMemo(() => user?.roles?.[0] ?? OtherRoles.UNKNOWN, [user?.roles])
  const isReadOnly = useMemo(() => Array.isArray(user?.roles) && recursiveIncludes(user?.roles, [InstitutionRoles.OBSERVER, SchoolRoles.DOMAIN_EVALUATOR]), [user?.roles])
  const sectionsObject = useMemo(() => arrayToObject(sections, 'id'), [sections])
  const sectionToYearsBySection = useMemo(() => reduceBySection(sectionToYears), [sectionToYears])
  const newSectionToYears = useMemo(() => sectionToYears.filter(sty => sty.section !== null), [sectionToYears])
  const specialityYears = useMemo(() => specialities.reduce((acc, speciality) => {
    speciality.specialityYears.forEach(sy => acc.push({ ...sy, speciality }))

    return acc
  }, []).sort(sortBySpecialityAndYear), [specialities])

  const permissions = useMemo(() => ({
    canCreate: isAdminReseau(user) || isLocalSecretary(user),
    canResetPassword: isAdminReseau(user) || isLocalSecretary(user),
    canDelete: isAdminReseau(user) || isLocalSecretary(user),
    canAddSpeciality: !(isAdminHospitalRO(user) || isClinicalSecretary(user) || isInternshipMasterMaccs(user) || isPresiMaster(user))
  }), [user])

  useEffect(() => {
    if (MACCS_CONTEXT.includes(user.context)) {
      setCurrentDate(moment().startOf('month').hours(0).minutes(0).seconds(0))
    } else {
      setCurrentDate(moment().startOf('isoWeek').hours(0).minutes(0).seconds(0))
    }
  }, [user.context])

  useEffect(() => { getAllLanguages().then(setLanguages) }, [])

  useEffect(() => {
    if (user?.id) {
      getAllAreas(user).then(json => {
        if (json?.data) {
          setAreas(json.data)
        }
      })
    }
  }, [user.id])

  useEffect(() => {
    if (user?.id) {
      setLoading(true)
      const promisesStack = []

      promisesStack.push(getConstants(user).then(json => {
        if (json?.data) {
          setEventCodes({ data: json.data.eventCodes, status: VALID_STATUS })
          setInstitutions(json.data.institutions.sort(sortByName))
          setOptins(getOptinsType(json.data.optins))
          setPauses(json.data.pauses)
          setSections(json.data.sections.sort(sortByAbbreviation))
          setSectionToYears(json.data.sectionToYears.sort(sortBySectionAndYear))
          setCountries(json.data.countries)
          setSchoolYears(json.data.schoolYears)
          setStudentMetadata(json.data.studentMetadata)
          setTutorials(getTutorialsByUrl(json.data.tutorials))

          if (!user.roles.includes(OtherRoles.ADMIN)) {
            setUnseenChangelogs(json.data.changelogs)
          }
        }
      }))
      promisesStack.push(getUserUnreadMessages(user).then(json => {
        if (json && isNumber(json.data)) {
          setUnreadMessages(json.data)
        }
      }))

      if (MACCS_CONTEXT.includes(user.context) || isInstitutionGroupAdmin(user)) {
        promisesStack.push(getAllSpecialities(user).then(({ data }) => setSpecialities(data)))
        promisesStack.push(getAllWorkingRegimes(user).then(({ data }) => setWorkingRegimes(data)))
      }

      if (user.profileImageName && !user.roles.includes('ROLE_ASSISTANT')) {
        getProfileImage(user).then(img => {
          if (img) {
            setProfileImage(img)
          }
        }).catch(e => {
          if (e.status !== HTTP_NOT_FOUND) {
            onError(t('Impossible to retrieve your profile picture'))
          }

          setProfileImage(null)
        })
      }

      resolvePromises(promisesStack).then(() => setLoading(false))
    }

    return () => {
      setLoading(true)
    }
  }, [user.id])

  useEffect(() => {
    if (eventCodes.status === INVALID_STATUS) {
      getAllEventCodes(user).then(data => setEventCodes({ data, status: VALID_STATUS }))
    }
  }, [user.id, eventCodes.status])

  const getTutorialsByUrl = tutorials => {
    const tutorialsByUrl = {}

    tutorials.forEach(t => {
      tutorialsByUrl[t.link] = t
    })

    return tutorialsByUrl
  }

  const getOptinsType = optins => {
    return optins.map(o => {
      if (DATA_SHARING_TEXTS.includes(o.text)) {
        o.type = DATA_SHARING_TYPE
      } else if (CARPOOL_OPTIN_TEXTS.includes(o.text)) {
        o.type = CARPOOL_TYPE
      } else {
        o.type = UNSUPPORTED_TYPE
      }

      return o
    })
  }

  const fetchOptins = user => {
    return getUserOptins(user).then(json => {
      if (json?.data) {
        setOptins(getOptinsType(json.data))
      }
    })
  }

  const fetchOptinsWithLoading = user => {
    setLoading(true)

    return fetchOptins(user).then(() => setLoading(false))
  }

  const toggleBetaMode = () => {
    setBetaMode(!betaMode)

    if (betaMode) {
      history.push(Routes.INTERNSHIPS)
    }
  }

  useEffect(() => {
    if (user?.id && (isEncoder(user) || isHospitalAdmin(user))) {
      getAllLinkRequests(user).then(({ data }) => setPendingLinkRequests(data.filter(lr => lr.status === LinkRequestStates.PENDING)))
    }
  }, [user?.id])

  return (
    <GlobalContext.Provider
      value={{
        betaMode: { active: betaMode, toggle: toggleBetaMode },
        context,
        countries,
        currentDate,
        currentRole,
        globalLoading: loading,
        environment,
        isDraft,
        isReadOnly,
        eventCodes: eventCodes.data,
        institutions,
        languages,
        newSectionToYears,
        nursesMode,
        modules,
        optins,
        pendingLinkRequests,
        profileImage,
        specialities,
        sections,
        pauses,
        permissions,
        schoolYears,
        sectionsObject,
        sectionToYears,
        sectionToYearsBySection,
        specialityYears,
        studentMetadata,
        tutorials,
        unreadMessages,
        unSeenChangelogs,
        user,
        workingRegimes,
        WIPZone,
        loading,
        fetchOptins: fetchOptinsWithLoading,
        invalidateEventCodes: () => setEventCodes({ ...eventCodes, status: INVALID_STATUS }),
        setCurrentDate,
        setEnvironment,
        setInstitutions,
        setIsDraft,
        setModules,
        setPendingLinkRequests,
        setUnreadMessages,
        setUnseenChangelogs,
        setTutorials,
        areas
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

export default GlobalProvider
